import React, { createContext, useContext, useEffect, useState } from 'react';
import { signInWithRedirect, getCurrentUser, signOut, fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import { setAuthContext } from '../functions/authHelper';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [awsConfigured, setAwsConfigured] = useState(false);

  useEffect(() => {
    // Wait for AWS configuration to be available
    const checkAwsConfig = () => {
      // Since we're now importing the config directly, we can assume it's configured
      setAwsConfigured(true);
      checkAuthState();
    };
    
    checkAwsConfig();
  }, []);

  const checkAuthState = async () => {
    try {
      // First check if there's a valid session
      const session = await fetchAuthSession();
      if (!session.tokens) {
        throw new Error('No valid session found');
      }
      
      const currentUser = await getCurrentUser();
      const userAttributes = await fetchUserAttributes();
      
      // Combine user info with attributes
      const userWithAttributes = {
        ...currentUser,
        attributes: userAttributes
      };
      
      setUser(userWithAttributes);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('User is not authenticated:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async () => {
    try {
      await signInWithRedirect();
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    }
  };

  const handleSignOut = async () => {
    try {
      // Clear local state first
      setUser(null);
      setIsAuthenticated(false);
      
      // Use timeout to ensure sign out completes properly
      setTimeout(async () => {
        try {
          await signOut();
          // After successful sign out, let the app handle the redirect based on auth state
        } catch (error) {
          console.error('Error signing out:', error);
          // Even if sign out fails, the app will handle the redirect
        }
      });
      
    } catch (error) {
      console.error('Error in handleSignOut:', error);
      // If there's an error, clear state and let the app handle the redirect
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const currentUser = await getCurrentUser();
      const userAttributes = await fetchUserAttributes();
      
      return {
        ...currentUser,
        attributes: userAttributes
      };
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  };

  const getCurrentSession = async () => {
    try {
      const session = await fetchAuthSession();
      return session;
    } catch (error) {
      console.error('Error getting current session:', error);
      return null;
    }
  };

  const getAccessToken = async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens.accessToken.toString();
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  };

  const getUserId = () => {
    if (user && user.username) {
      return user.username;
    }
    // If no user or no username, check if we're still loading
    if (loading) {
      return null; // Still loading, wait
    }
    // If not loading and no user, user is not authenticated
    return null;
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    awsConfigured,
    signIn: handleSignIn,
    signOut: handleSignOut,
    getCurrentUser: getCurrentUserInfo,
    getCurrentSession,
    getAccessToken,
    getUserId,
    checkAuthState,
  };

  // Set the auth context in the helper
  setAuthContext(value);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 